import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { TeachersAvailabilityApi } from "@/openapi/generated";
import type { CreateSpecialHourRequest } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const useSpecialHoursCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (createSpecialHourRequest: CreateSpecialHourRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(
        TeachersAvailabilityApi,
        accessToken,
      );
      return service.apiAvailabilitySpecialAppointmentHoursPost({
        createSpecialHourRequest,
      });
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.SpecialHours, variables.teacherId],
      });
      toast({
        title: "Special hours created",
        description: "Special hours created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to create special hours";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
